import { Checkbox, Input, Tooltip } from "antd";
import { LuCircleHelp, LuInfo, LuMaximize2, LuMinimize2, LuPlus } from "react-icons/lu";
import PropTypes from "prop-types";
import React from "react";
import { isEmpty } from "lodash";
import CustomButton from "../../../commonComponents/button";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";
import QuickTipCheckbox from "./QuickTipCheckbox";
import ToneChip from "./ToneChip";
import { AI_RESPONSE_COPY } from "./constants";

/**
 * Shared presentational card for the "Customize AI response" experience.
 * Rendered IDENTICALLY inside both the compact Popover and the maximized Drawer
 * (EAI-1497) — only the outer container differs. All state/data/handlers are
 * owned by the CustomizeAiResponse controller and passed down.
 */
export default function CustomizePanelContent({
  isMaximized,
  onToggleMaximize,
  onOpenHideUnhide,
  quickTipLoader,
  tips,
  tones,
  emptyTipsText,
  emptyTonesText,
  value,
  onToggleTip,
  onToggleTone,
  onChangeNote,
  additionalCommentsEnabled,
  showSavePref,
  savePref,
  onSavePrefChange,
  savingPref,
  generating,
  onGenerate,
  isOwnCustom,
  onAddTip,
  onAddTone,
  onEditItem,
  onDeleteItem,
}) {
  const AddButton = ({ label, onClick }) => (
    <button
      type="button"
      aria-label={`Add ${label}`}
      className="flex items-center justify-center w-5 h-5 rounded border border-dashed border-[#D9D9D9] bg-transparent cursor-pointer text-grey-600 hover:border-[#1677FF] hover:text-[#1677FF]"
      onClick={onClick}
    >
      <LuPlus className="text-xs" />
    </button>
  );

  const SectionHeader = ({ label, onAdd, rightAction }) => (
    <div className="flex flex-row items-center justify-between w-full">
      <div className="flex items-center gap-2">
        <span className="text-sm font-[700] text-grey-700">{label}</span>
        <AddButton label={label} onClick={onAdd} />
      </div>
      {rightAction}
    </div>
  );

  return (
    <div className="flex flex-col flex-1 min-h-0" onClick={e => e.stopPropagation()}>
      {/* Header — identical across popover & drawer */}
      <div className="flex items-center justify-between p-3 bg-[#F5F5F5] border-0 border-b border-solid border-[#D9D9D9]">
        <span className="text-sm font-[500] text-grey-700">{AI_RESPONSE_COPY.CUSTOMIZE_TITLE}</span>
        <Tooltip title={isMaximized ? AI_RESPONSE_COPY.MINIMIZE : AI_RESPONSE_COPY.MAXIMIZE}>
          <span className="flex items-center cursor-pointer" onClick={onToggleMaximize}>
            {isMaximized ? (
              <LuMinimize2 className="text-[16px] text-black/45" />
            ) : (
              <LuMaximize2 className="text-[16px] text-black/45" />
            )}
          </span>
        </Tooltip>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-5 px-5 pt-5 pb-2 flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col items-start gap-4">
          <SectionHeader
            label={AI_RESPONSE_COPY.QUICK_TIPS}
            onAdd={onAddTip}
            rightAction={
              <button
                type="button"
                className="bg-transparent border-0 p-0 cursor-pointer text-sm text-brand-600"
                onClick={onOpenHideUnhide}
              >
                {AI_RESPONSE_COPY.HIDE_UNHIDE_LINK}
              </button>
            }
          />
          <div className="flex flex-col w-full">
            {quickTipLoader ? (
              <CustomSkeleton skeletonCount={1} />
            ) : !isEmpty(tips) ? (
              tips.map(item => {
                const own = isOwnCustom(item);
                return (
                  <QuickTipCheckbox
                    key={item.id}
                    item={{ ...item, isCustom: own }}
                    checked={value.quickTipIds.includes(item.id)}
                    onToggle={onToggleTip}
                    onEdit={own ? onEditItem : null}
                    onDelete={own ? onDeleteItem : null}
                  />
                );
              })
            ) : (
              <span className="text-sm text-grey-600">{emptyTipsText}</span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-start gap-4">
          <SectionHeader label={AI_RESPONSE_COPY.TONES} onAdd={onAddTone} />
          <div className="flex flex-row flex-wrap gap-2">
            {quickTipLoader ? (
              <CustomSkeleton skeletonCount={1} />
            ) : !isEmpty(tones) ? (
              tones.map(item => {
                const own = isOwnCustom(item);
                return (
                  <ToneChip
                    key={item.id}
                    label={item.tip}
                    active={value.toneIds.includes(item.id)}
                    isCustom={own}
                    onClick={() => onToggleTone(item)}
                    onEdit={own ? () => onEditItem(item) : null}
                    onDelete={own ? () => onDeleteItem(item) : null}
                  />
                );
              })
            ) : (
              <span className="text-sm text-grey-600">{emptyTonesText}</span>
            )}
          </div>
        </div>

        {additionalCommentsEnabled && (
          <div className="flex flex-col items-start gap-4">
            <div className="flex items-center gap-1">
              <span className="text-sm font-[400] text-grey-700">
                {AI_RESPONSE_COPY.ADDITIONAL_NOTES}
              </span>
              <Tooltip title={AI_RESPONSE_COPY.ADDITIONAL_NOTES_TOOLTIP}>
                <LuCircleHelp className="text-grey-400 text-sm" />
              </Tooltip>
              <span className="text-sm text-grey-400">
                {AI_RESPONSE_COPY.ADDITIONAL_NOTES_MARK}
              </span>
            </div>
            <Input.TextArea
              value={value.additionalNote}
              onChange={e => onChangeNote(e.target.value)}
              autoSize={{ minRows: 3 }}
              placeholder={AI_RESPONSE_COPY.ADDITIONAL_NOTES_PLACEHOLDER}
            />
          </div>
        )}
      </div>

      {/* Footer — identical across popover & drawer */}
      <div className="flex flex-col gap-3 px-5 pb-5 pt-3">
        {showSavePref && (
          <div className="flex items-center gap-2 rounded-lg border-0  bg-[#F5F5F5] p-2">
            <Checkbox
              checked={savePref}
              disabled={savingPref}
              onChange={e => onSavePrefChange(e.target.checked)}
            >
              {AI_RESPONSE_COPY.SAVE_PREFERENCES_FUTURE}
            </Checkbox>
            <Tooltip title={AI_RESPONSE_COPY.SAVE_PREFERENCES_TOOLTIP}>
              <LuInfo className="text-grey-400" />
            </Tooltip>
          </div>
        )}
        <CustomButton
          btnText={AI_RESPONSE_COPY.GENERATE}
          loading={generating}
          handleClick={onGenerate}
          type="primary"
          className="w-full rounded-md shadow-[0px_2px_0px_rgba(5,145,255,0.1)]"
        />
      </div>
    </div>
  );
}

const itemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tip: PropTypes.string,
  suggestionType: PropTypes.string,
});

CustomizePanelContent.propTypes = {
  isMaximized: PropTypes.bool,
  onToggleMaximize: PropTypes.func.isRequired,
  onOpenHideUnhide: PropTypes.func.isRequired,
  quickTipLoader: PropTypes.bool,
  tips: PropTypes.arrayOf(itemShape),
  tones: PropTypes.arrayOf(itemShape),
  emptyTipsText: PropTypes.string,
  emptyTonesText: PropTypes.string,
  value: PropTypes.shape({
    quickTipIds: PropTypes.arrayOf(PropTypes.number),
    toneIds: PropTypes.arrayOf(PropTypes.number),
    additionalNote: PropTypes.string,
  }).isRequired,
  onToggleTip: PropTypes.func.isRequired,
  onToggleTone: PropTypes.func.isRequired,
  onChangeNote: PropTypes.func.isRequired,
  additionalCommentsEnabled: PropTypes.bool,
  showSavePref: PropTypes.bool,
  savePref: PropTypes.bool,
  onSavePrefChange: PropTypes.func.isRequired,
  savingPref: PropTypes.bool,
  generating: PropTypes.bool,
  onGenerate: PropTypes.func.isRequired,
  isOwnCustom: PropTypes.func.isRequired,
  onAddTip: PropTypes.func.isRequired,
  onAddTone: PropTypes.func.isRequired,
  onEditItem: PropTypes.func.isRequired,
  onDeleteItem: PropTypes.func.isRequired,
};

CustomizePanelContent.defaultProps = {
  isMaximized: false,
  quickTipLoader: false,
  tips: [],
  tones: [],
  emptyTipsText: AI_RESPONSE_COPY.NO_TIPS,
  emptyTonesText: AI_RESPONSE_COPY.NO_TONES,
  additionalCommentsEnabled: false,
  showSavePref: false,
  savePref: false,
  savingPref: false,
  generating: false,
};
